import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/router";
import {
  type ComponentType,
  type FC,
  type ReactNode,
  useEffect,
  useRef,
} from "react";
import { LoadingBox } from "src/components/LoadingBox";
import { AuthorizedUserProvider } from "src/features/auth/AuthorizedUserProvider";
import { useAuth } from "src/features/auth/AuthProvider";
import { useIdApp } from "src/hooks/useIdApp";
import { pagesPath } from "src/utils/$path";
import { ErrorScreen } from "../ErrorScreen";

export const AuthorizedGuard: FC<{
  children: ReactNode;
  workspaceId: string | null;
  AuthenticatedWrapper?: ComponentType<{ children: ReactNode }>;
}> = ({ children, workspaceId, AuthenticatedWrapper }) => {
  const router = useRouter();
  const { authState, check } = useAuth();
  const {
    redirectToSignInUrl,
    redirectToSignOutUrl,
    redirectToSelectWorkspaceUrl,
  } = useIdApp();
  const checked = useRef(false);

  useSentryUser();

  // 認証状態が不確定であれば、チェックを行う
  useEffect(() => {
    if (authState.status !== "unknown") {
      return;
    }
    if (checked.current) {
      return;
    }
    checked.current = true;

    check();
  }, [authState.status, check]);

  // 認証状態が未認証であれば、ログイン画面に移動する
  useEffect(() => {
    if (authState.status === "unauthenticated") {
      if (process.env.NEXT_PUBLIC_ENABLE_DEBUG_LOGIN === "true") {
        void router.push({
          pathname: pagesPath.debug.auth.$url().pathname,
          query: { return_path: router.asPath },
        });
        return;
      }
      redirectToSignInUrl();
      return;
    }
    if (
      workspaceId == null &&
      (authState.status === "current_shop_not_set" ||
        (authState.status === "ok" && authState.user.currentWorkspace == null))
    ) {
      redirectToSelectWorkspaceUrl();
    }
  }, [
    workspaceId,
    authState.status,
    authState.user?.currentWorkspace,
    redirectToSignInUrl,
    redirectToSelectWorkspaceUrl,
    router,
  ]);

  const selectAnotherWorkspace = () =>
    redirectToSelectWorkspaceUrl({
      returnUrl: `${window.location.origin}/`,
    });

  if (authState.status === "ip_restricted") {
    return (
      <ErrorScreen
        message="このワークスペースは許可されたネットワークからのみ利用できます。"
        onSelectWorkspace={selectAnotherWorkspace}
        showHomeLink
      />
    );
  }

  if (
    authState.status === "workspace_forbidden" ||
    (authState.status === "ok" &&
      workspaceId != null &&
      authState.user.currentWorkspace != null &&
      authState.user.currentWorkspace.id !== workspaceId)
  ) {
    return (
      <ErrorScreen
        message="このワークスペースにアクセスする権限がありません。"
        onSelectWorkspace={selectAnotherWorkspace}
        showHomeLink
      />
    );
  }
  if (
    workspaceId != null &&
    (authState.status === "current_shop_not_set" ||
      authState.status === "service_inactive" ||
      authState.status === "unauthorized" ||
      (authState.status === "ok" && authState.user.currentWorkspace == null))
  ) {
    return (
      <ErrorScreen
        message="このワークスペースではこのサービスを利用できません。"
        onSelectWorkspace={selectAnotherWorkspace}
        showHomeLink
      />
    );
  }
  if (authState.status === "service_inactive") {
    return (
      <ErrorScreen message="このサービスは利用できません。" showHomeLink />
    );
  }
  if (authState.status === "unauthorized") {
    return (
      <ErrorScreen
        message="このページにアクセスする権限がありません。"
        onReauthenticate={() => redirectToSignOutUrl()}
      />
    );
  }
  if (authState.status === "error") {
    return (
      <ErrorScreen
        message="エラーが発生しました。再読み込みを行ってください。"
        onReauthenticate={() => redirectToSignOutUrl()}
      />
    );
  }
  if (authState.user != null && authState.user.currentWorkspace != null) {
    const authenticatedChildren = AuthenticatedWrapper ? (
      <AuthenticatedWrapper>{children}</AuthenticatedWrapper>
    ) : (
      children
    );

    return (
      <AuthorizedUserProvider
        currentUser={authState.user}
        currentWorkspace={authState.user.currentWorkspace}
      >
        {authenticatedChildren}
      </AuthorizedUserProvider>
    );
  }

  return <LoadingBox />;
};

const useSentryUser = () => {
  const { authState } = useAuth();

  useEffect(() => {
    if (authState.status !== "ok") {
      return;
    }
    Sentry.setUser({
      id: authState.user.id,
    });

    if (authState.user.currentWorkspace != null) {
      Sentry.setTag("workspace_id", authState.user.currentWorkspace.id);
    }

    return () => {
      Sentry.setUser(null);
      Sentry.setTag("workspace_id", null);
    };
  }, [authState]);
};
